import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/context/AuthContext';
import { usePatientProfileContext } from 'app/my-account/pages/patient/PatientProfileContext';
import { useVerifyChild } from 'app/my-account/pages/child/useVerifyChild';
import { ChildGenderEnum } from 'app/my-account/pages/add-child/useCreateSubPatient';
import { DASHBOARD } from 'utilities/routes';
import { ReactComponent as LogoIcon } from 'assets/icons/sgbnet.svg';
import { twMerge } from 'tailwind-merge';
import UnverifiedChildNavbar from 'components/navbar/UnverifiedChildNavbar';
import { AuthenticatedNavbar } from 'components/navbar/AuthenticatedNavbar';
import JoinToAppointmentOutlet from 'components/navbar/JoinToAppointmentOutlet';
import ScheduledDnaTestOutlet from 'components/navbar/ScheduledDnaTestOutlet';

const childHeaderColorClasses = new Map<string, string>([
  [ChildGenderEnum.MALE, 'from-[#92E4FA80]'],
  [ChildGenderEnum.FEMALE, 'from-[#FFCEFFFF]'],
]);

interface PatientNavbarProps {
  openNavbarMobile: () => void;
}

export const PatientNavbar: React.FC<PatientNavbarProps> = ({
  openNavbarMobile,
}) => {
  const navigate = useNavigate();
  const { selectedAccount } = useAuth();
  const { profileData } = usePatientProfileContext();
  const { isVerify } = useVerifyChild();

  const isChildGradient = Boolean(selectedAccount && profileData?.gender);

  return (
    <div className="w-full z-[999] relative desktop:min-w-[1300px] box-border bg-white border-b shrink-0 p-0">
      <div className="w-full h-2.5 bg-clc-blue" />
      <div
        className={twMerge(
          'w-full h-[70px] desktop:h-[90px] px-7 2xl:px-[135px] flex justify-between items-center gap-4',
          isChildGradient &&
            `bg-gradient-to-b ${childHeaderColorClasses.get(
              profileData?.gender as string,
            )} via-50% to-white to-50%`,
        )}
        data-testid="navbar-patient"
      >
        <LogoIcon
          className="w-[120px] h-[27px] desktop:min-w-[160px] desktop:h-9 cursor-pointer"
          onClick={() => navigate(DASHBOARD)}
        />

        <div className="ml-auto">
          {!isVerify && selectedAccount ? (
            <UnverifiedChildNavbar openNavbarMobile={openNavbarMobile} />
          ) : (
            <AuthenticatedNavbar openNavbarMobile={openNavbarMobile} />
          )}
        </div>
      </div>

      <JoinToAppointmentOutlet />
      <ScheduledDnaTestOutlet />
    </div>
  );
};
